import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { Layout } from './layout/Layout'
import { isBareTicketsRoute } from './lib/tickets-routes'
import { RequireAuth } from './components/RequireAuth'
import { LinktreePage } from './pages/LinktreePage'

/** How long to keep looking for a `#hash` target that renders after a fetch. */
const HASH_TARGET_TIMEOUT_MS = 5000
const HASH_TARGET_POLL_MS = 100

function ScrollToTop() {
  const { pathname, hash } = useLocation()
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0)
      return
    }
    // Deep links like /events/slug#galerie point at content that only exists
    // once the event query resolves, so poll briefly instead of giving up.
    const id = decodeURIComponent(hash.slice(1))
    const smooth = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const started = Date.now()
    const timer = window.setInterval(() => {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' })
        window.clearInterval(timer)
        return
      }
      if (Date.now() - started > HASH_TARGET_TIMEOUT_MS) window.clearInterval(timer)
    }, HASH_TARGET_POLL_MS)
    return () => window.clearInterval(timer)
  }, [pathname, hash])
  return null
}
import { HomePage } from './pages/HomePage'
import { EventsPage } from './pages/EventsPage'
import { EventDetailPage } from './pages/EventDetailPage'
import { ArtistsPage } from './pages/ArtistsPage'
import { ArtistDetailPage } from './pages/ArtistDetailPage'
import { AboutPage } from './pages/AboutPage'
import { ContactPage } from './pages/ContactPage'
import { LegalPage } from './pages/LegalPage'
import { ReleasesPage } from './pages/ReleasesPage'
import { FaqPage } from './pages/FaqPage'
import { LoginPage } from './pages/LoginPage'
import { MemberPage } from './pages/MemberPage'
import { EventTicketsPage } from './pages/EventTicketsPage'
import { EventTicketsSuccessPage } from './pages/EventTicketsSuccessPage'
import { EventTicketsErrorPage } from './pages/EventTicketsErrorPage'

const ADMIN_FRONTEND_URL = import.meta.env.VITE_ADMIN_URL || '/admin-app'

function AdminRedirect() {
  useEffect(() => {
    window.location.href = ADMIN_FRONTEND_URL
  }, [])
  return (
    <div className="wrap" style={{ paddingTop: 'var(--sp-8)' }}>
      <p className="muted" style={{ fontFamily: 'var(--mono)', letterSpacing: '.14em', fontSize: 13 }}>
        REDIRECTING…
      </p>
    </div>
  )
}

// Bare linktree on links.bnkr.ch (and legacy link.bnkr.ch alias) — no site nav/footer
const isLinktreeHost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'links.bnkr.ch' || window.location.hostname === 'link.bnkr.ch')

function SiteRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/links" element={<LinktreePage />} />
        <Route path="/" element={<HomePage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/tickets/success" element={<EventTicketsSuccessPage />} />
        <Route path="/events/tickets/error" element={<EventTicketsErrorPage />} />
        <Route path="/events/:slug/tickets" element={<EventTicketsPage />} />
        <Route path="/events/:slug" element={<EventDetailPage />} />
        <Route path="/releases" element={<ReleasesPage />} />
        <Route path="/artists" element={<ArtistsPage />} />
        <Route path="/artists/:slug" element={<ArtistDetailPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/team" element={<Navigate to="/about#team" replace />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/impressum" element={<LegalPage slug="impressum" titleFallback="Impressum" />} />
        <Route
          path="/datenschutz"
          element={<LegalPage slug="datenschutzerklaerung" titleFallback="Datenschutz" />}
        />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/member"
          element={
            <RequireAuth>
              <MemberPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <AdminRedirect />
            </RequireAuth>
          }
        />
      </Routes>
    </>
  )
}

export function App() {
  if (isLinktreeHost) return <LinktreePage />

  const { pathname } = useLocation()
  if (isBareTicketsRoute(pathname)) {
    return <SiteRoutes />
  }

  return (
    <Layout>
      <SiteRoutes />
    </Layout>
  )
}
